import angular from "angular";

import { CalculatorLogic, Dates, Numeric, ReportType, TaxCard2019Logic, Token } from "@salaxy/core";
import { CalculationMapper, Templates } from "@salaxy/reports";
import { SessionService } from "../../services";
import jsonTest from "./test.json";

/**
 * Helps in performing basic smoke test about salaxy libraries:
 * Are all the libraries and dependencies loaded and functional in the package.
 */
export class SmokeTestController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SessionService"];

  /** Defines the view / set of tests: "core" or "ng1" */
  public mode: "core" | "ng1";

  public reportsTests = {
    loaded: null as boolean,
    mapper: null as boolean,
    handlebars: null as boolean
  };

  constructor(public sessionSrv: SessionService) {
  }

  /**
   * Controller initialization
   */
  public $onInit() {
    try {
      this.runReportTests();
    }
    catch(e)
    {
      console.error("Init tests run failed with exception.");
      console.log(e);
    }
  }

  /** Gets number 1 from @salaxy/core library. */
  public getNumber(): 1 | number {
    return Numeric.parseNumber("1") || null;
  }

  /** Gets a date formatted with Dates library: "20.02.2020" */
  public getADate(): "20.02.2020" | string {
    return Dates.format(Dates.asDate("2020-02-20"));
  }

  /** Returns true if the code can read the token usin Base64 library. */
  public readToken(): boolean {
    const token = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJPbmxpbmUgSldUIEJ1aWxkZXIiLCJpYXQiOjE2Mjg2OTE2OTMsImV4cCI6MTY2MDIyNzY5MywiYXVkIjoid3d3LmV4YW1wbGUuY29tIiwic3ViIjoianJvY2tldEBleGFtcGxlLmNvbSIsIkdpdmVuTmFtZSI6IkpvaG5ueSIsIlN1cm5hbWUiOiJSb2NrZXQiLCJFbWFpbCI6Impyb2NrZXRAZXhhbXBsZS5jb20iLCJSb2xlIjpbIk1hbmFnZXIiLCJQcm9qZWN0IEFkbWluaXN0cmF0b3IiXX0.WBuoHS5h8iZi0b5cE25onpvgYFv_X-QXbLdf0KjECEY";
    return Token.parseHeader(token).alg === "HS256";
  }

  /** Runs smoke tests related to Reports library. */
  public runReportTests(): void {
    this.reportsTests.loaded = !!new CalculationMapper();
    this.reportsTests.mapper = this.isCalcMapperWorking();
    this.reportsTests.handlebars = this.isHbsTemplatesWorking();
  }

  /** Shows a saveAs dialog for text file ("Hello, world!"). */
  public saveAs() {
    const blob = new Blob(["Hello, world!"], {type: "text/plain;charset=utf-8"});
    // This only works in non-module mode (umd package). Implement module version if necessary.
    (window as any).saveAs(blob, "hello world.txt");
  }

  /** Gets a text from json file. */
  public getJson() {
    return jsonTest.test;
  }

  /** Test chart with annotations */
  public chartData: any = TaxCard2019Logic.getMainChart(
    {
      card: {
        incomeLimit: 30000,
        taxPercent: 10,
        taxPercent2: 30,
        forYear: Dates.getYear(Dates.getToday()),
        personalId: "111111-111*",
        validity: {
          start: Dates.getToday(),
        }
     },
     result: {
       totalIncome: 21000,
     },
     incomeLog: [],
    }
  );
  private isCalcMapperWorking(): boolean {
    const calc = CalculatorLogic.getBlank();
    calc.worker.avatar.firstName = "James";
    calc.worker.avatar.sortableName = "JamesB";
    calc.worker.tax.snapshot = { forYear: 2021, incomeLimit: 999.50 };
    calc.result.totals = {};
    calc.result.workerCalc = {};
    calc.result.responsibilities = {};
    calc.result.employerCalc = {};
    const calcReport = CalculationMapper.getCalculationReport(calc, ReportType.SalarySlip, undefined);

    return (
      calcReport.header.taxcardIncomeLimit === 999.50
      && calcReport.header.worker.avatar.firstName === "James"
      && calcReport.header.worker.avatar.sortableName === "JamesB"
    );
  }

  /**
   * Tests that handlebars templates including nested partials and helpers are working correctly.
   * @returns Returns true if templates are working and otherwise false.
   */
  private isHbsTemplatesWorking(): boolean {
    const html = new Templates().getHtml(
      "salarySlipV2",
      {
        report: {
          header: {
            taxcardIncomeLimit: 999.50,
            worker: {
              avatar: {
                firstName: "James",
                sortableName: "JamesB",
              }
            }
          }
        }
      }
    );

    return (
      // Produced by a deeply nested partial (salarySlipV2 -> workerReportHeadV2 ->
      // contractPartiesContactDetailsV2)
      html.indexOf(`<div class="uppercase">JamesB</div>`) !== -1
      // Produced by a helper function used in a deeply nested partial (salarySlipV2 ->
      // workerReportHeadV2 -> workerContractDetailsV2 -> c
      && html.indexOf(`<span>999,50€</span>`) !== -1
    );
  }

}
